/**
 * 날짜별 남은 수량 달력 (교회 화면 · 관리자 화면 공용)
 *  - 토요일 · 주일 · 공휴일은 빗금 (수령·반납 불가)
 *  - 칸마다 물품별 남은 수량: 넓은 화면은 물품 이름, 좁은 화면은 ①②③ 번호
 */
import React from 'react';
import { Box, Button, IconButton, Typography, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { offReason, remainingOn, OccReservation, Holiday } from './logic';
import { DOW_LABELS, RentalItem } from './api';

const pad = (n: number) => (n < 10 ? '0' : '') + n;
const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨'];

type Props = {
  month: string; // YYYY-MM
  onMonth: (m: string) => void;
  selected: string;
  onSelect: (d: string) => void;
  items: RentalItem[];
  reservations: OccReservation[];
  holidays: Holiday[];
  today: string;
  filter: string; // 'all' | itemId
  onFilter: (f: string) => void;
};

const RentalCalendar: React.FC<Props> = ({ month, onMonth, selected, onSelect, items, reservations, holidays, today, filter, onFilter }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const dark = theme.palette.mode === 'dark';
  const shown = items.filter((i) => filter === 'all' || i._id === filter);
  const [y, m] = month.split('-').map(Number);
  const firstDow = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const move = (delta: number) => {
    const d = new Date(Date.UTC(y, m - 1 + delta, 1));
    onMonth(`${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`);
  };
  const tone = {
    ok: { bg: alpha(theme.palette.success.main, dark ? 0.18 : 0.14), fg: dark ? '#86efac' : '#166534' },
    part: { bg: alpha(theme.palette.warning.main, dark ? 0.2 : 0.16), fg: dark ? '#fcd34d' : '#92400e' },
    full: { bg: alpha(theme.palette.text.primary, 0.08), fg: theme.palette.text.secondary },
  };
  const cellBg = dark ? '#353a45' : '#f1f5f9';
  const offBg = dark
    ? 'repeating-linear-gradient(135deg,#2b2f38 0 6px,#323743 6px 8px)'
    : 'repeating-linear-gradient(135deg,#eef1f5 0 6px,#e2e7ee 6px 8px)';

  const cells: React.ReactNode[] = [];
  for (let b = 0; b < firstDow; b += 1) cells.push(<Box key={`b${b}`} />);
  for (let d = 1; d <= days; d += 1) {
    const s = `${y}-${pad(m)}-${pad(d)}`;
    const off = offReason(s, holidays);
    const past = s < today;
    if (off) {
      cells.push(
        <Box key={s} sx={{ minHeight: { xs: 74, sm: 96 }, borderRadius: 1.5, p: 0.75, background: offBg, color: 'text.disabled', opacity: past ? 0.5 : 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
            <Typography fontSize={13} fontWeight={700}>{d}</Typography>
            {!narrow && off !== '토요일' && <Typography fontSize={10.5} color="error.light" noWrap>{off}</Typography>}
          </Box>
        </Box>,
      );
      continue;
    }
    cells.push(
      <Box key={s} component="button" type="button" onClick={() => onSelect(s)} aria-label={`${m}월 ${d}일`}
        sx={{
          minHeight: { xs: 74, sm: 96 }, borderRadius: 1.5, p: { xs: 0.4, sm: 0.75 }, bgcolor: cellBg, border: 0, textAlign: 'left', cursor: 'pointer',
          color: 'text.primary', display: 'flex', flexDirection: 'column', gap: 0.35, minWidth: 0, opacity: past ? 0.5 : 1, font: 'inherit',
          outline: selected === s ? `2px solid ${theme.palette.warning.main}` : 'none',
        }}>
        <Typography fontSize={13} fontWeight={700}>{d}</Typography>
        {shown.map((it, k) => {
          const n = remainingOn({ id: it._id, total: it.total, repair: it.repair }, s, reservations, today);
          const cap = (it.total || 0) - (it.repair || 0);
          const t = n <= 0 ? tone.full : n < cap ? tone.part : tone.ok;
          return (
            <Box key={it._id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5, borderRadius: 1, px: { xs: 0.4, sm: 0.6 }, py: '1px', fontSize: { xs: 10, sm: 11 }, lineHeight: 1.25, bgcolor: t.bg, color: t.fg }}>
              {/* 넓은 화면은 물품 이름 전체(길면 두 줄), 좁은 화면은 ①②③ */}
              <span style={{ wordBreak: 'keep-all', minWidth: 0 }}>{narrow ? (shown.length > 1 ? CIRCLED[k] : '') : it.name}</span>
              <b style={{ whiteSpace: 'nowrap' }}>{n <= 0 ? '마감' : n}</b>
            </Box>
          );
        })}
      </Box>,
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => move(-1)} aria-label="이전 달"><ChevronLeft /></IconButton>
          <Typography fontWeight={800} fontSize={18}>{y}년 {m}월</Typography>
          <IconButton size="small" onClick={() => move(1)} aria-label="다음 달"><ChevronRight /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Button size="small" variant={filter === 'all' ? 'contained' : 'outlined'} color="warning" onClick={() => onFilter('all')}>전체</Button>
          {items.map((it) => (
            <Button key={it._id} size="small" variant={filter === it._id ? 'contained' : 'outlined'} color="warning" onClick={() => onFilter(it._id)}>{it.name}</Button>
          ))}
        </Box>
      </Box>
      {narrow && shown.length > 1 && (
        <Typography fontSize={12} color="text.secondary" mb={1}>
          {shown.map((it, k) => `${CIRCLED[k]} ${it.name}`).join(' · ')} 남은 수량
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, sm: 0.75 } }}>
        {DOW_LABELS.map((w, k) => (
          <Typography key={w} textAlign="center" fontSize={12} color={k === 0 ? 'error.light' : k === 6 ? 'info.light' : 'text.secondary'}>{w}</Typography>
        ))}
        {cells}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, fontSize: 12, color: 'text.secondary' }}>
        {[['여유', tone.ok.bg], ['일부 예약', tone.part.bg], ['마감', tone.full.bg]].map(([label, bg]) => (
          <span key={label}><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.5, bgcolor: bg, mr: 0.6, verticalAlign: -1 }} />{label}</span>
        ))}
        <span><Box component="i" sx={{ display: 'inline-block', width: 11, height: 11, borderRadius: 0.5, background: offBg, mr: 0.6, verticalAlign: -1 }} />토요일 · 주일 · 공휴일 (수령·반납 불가)</span>
      </Box>
    </Box>
  );
};

export default RentalCalendar;
